import { useNavigate } from 'react-router-dom';
import { LogOut, Settings, User as UserIcon, FolderKanban, ChevronUp } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  Avatar,
  cn,
} from '@/components/ui';
import { useAuth } from '@/auth/useAuth';

/*
  ProfileAvatarMenu — аватар-карточка в rail-bottom, drop-up меню.

  Sprint 1.11 fixes:
  - КРИТИЧНО: asChild + <Link> заменён на onSelect + useNavigate. Старый
    pattern вызывал white screen на клик из-за Radix Slot конфликта с
    multiple-children в DropdownMenuItem (правило 20 motion principles).
  - Avatar block теперь как visible card (background, border, rounded-xs)
    в expanded mode — соответствует Modulix reference.
*/

export function ProfileAvatarMenu({ expanded }: { expanded: boolean }) {
  const { state, logout } = useAuth();
  const navigate = useNavigate();
  if (state.status !== 'authenticated') return null;
  const user = state.user;

  // Navigation через useNavigate — НЕ через asChild + Link (causes white screen)
  const go = (path: string) => navigate(path);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Меню профиля"
          className={cn(
            'group/avatar flex h-12 w-full shrink-0 items-center rounded-md outline-none',
            'transition-colors duration-normal',
            expanded
              ? // Expanded — visible card как в Modulix
                'border border-rail-divider bg-rail-control-soft pr-2 ' +
                  'hover:border-rail-control-hover-line hover:bg-rail-control-hover-soft'
              : // Collapsed — без border, hover-bg
                'hover:bg-rail-hover',
          )}
        >
          {/* Avatar wrapper 48x48 — на одной линии с X и nav icons */}
          <span className="grid size-12 shrink-0 place-items-center">
            <Avatar
              src={user.avatar_url}
              name={user.display_name || user.email}
              size="md"
              className="ring-1 ring-white/15"
            />
          </span>
          <span
            className={cn(
              'flex min-w-0 flex-col items-start overflow-hidden text-left',
              'transition-rail-label duration-slow ease-in-out',
              expanded ? 'ml-1 max-w-profile-label flex-1 opacity-100 delay-100' : 'max-w-0 opacity-0',
            )}
          >
            <span className="w-full truncate text-sm font-medium text-rail-fg">
              {user.display_name || user.email}
            </span>
            <span className="w-full truncate text-2xs text-rail-fg-muted">{user.email}</span>
          </span>
          {expanded && (
            <ChevronUp
              size={14}
              className="shrink-0 text-rail-fg-muted transition-transform duration-normal group-hover/avatar:-translate-y-0.5"
            />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" sideOffset={8} className="min-w-60">
        <div className="flex items-center gap-3 p-2">
          <Avatar src={user.avatar_url} name={user.display_name || user.email} size="lg" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{user.display_name || user.email}</p>
            <p className="truncate text-xs text-fg-subtle">{user.email}</p>
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Личное</DropdownMenuLabel>
        <DropdownMenuItem leftIcon={<FolderKanban />} onSelect={() => go('/portfolio/me')}>
          Моё портфолио
        </DropdownMenuItem>
        <DropdownMenuItem leftIcon={<UserIcon />} onSelect={() => go('/settings/profile')}>
          Профиль
        </DropdownMenuItem>
        <DropdownMenuItem leftIcon={<Settings />} onSelect={() => go('/settings')}>
          Настройки
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem leftIcon={<LogOut />} destructive onSelect={() => void logout()}>
          Выйти
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
